import Lang from 'types/Lang';

/**
 * Client-side mirror of the server's rules, so the applicant is not round-tripped for a typo. Not a
 * security boundary: everything is re-checked server-side and the server wins.
 */

/** Same pattern the employee contact form uses. */
export const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/** Permissive, matching the server: a Belgian-only pattern would reject valid foreign numbers. */
export const PHONE_PATTERN = /^\+?[0-9][0-9\s.()-]{6,28}[0-9]$/;

export const NAME_MAX_LENGTH = 100;
export const EMAIL_MAX_LENGTH = 256;
export const PHONE_MAX_LENGTH = 30;
export const MOTIVATION_MAX_LENGTH = 2000;

export const CV_MAX_SIZE_MB = 5;
export const CV_MAX_SIZE_BYTES = CV_MAX_SIZE_MB * 1024 * 1024;

/** Exactly the set the backend accepts — .png is NOT allowed, though .jpg/.jpeg are. */
export const CV_ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg'];

export const CV_ACCEPT_ATTRIBUTE = CV_ALLOWED_EXTENSIONS.join(',');

export type CvRejectionReason = 'type' | 'size';

/**
 * `null` means acceptable; size is reported first as the coarser problem. Extension-based like the
 * server, because browsers often report no MIME type for .doc/.docx.
 */
export const validateCvFile = (file: File): CvRejectionReason | null => {
  if (file.size > CV_MAX_SIZE_BYTES) return 'size';

  const name = file.name.toLowerCase();
  if (!CV_ALLOWED_EXTENSIONS.some(extension => name.endsWith(extension))) return 'type';

  return null;
};

/**
 * The API's LanguageId: 1 = NL, 2 = FR, 3 = EN — the same numbering the Lang enum's order produces,
 * so reordering that enum breaks this.
 */
export const toLanguageId = (language: string): number => {
  // Normalised: 'nl-BE' would otherwise miss and silently send EN, which nobody would notice
  // until the promote mail goes out in the wrong language.
  const normalized = (language ?? '').slice(0, 2).toLowerCase();
  const index = Object.values(Lang).indexOf(normalized as Lang);
  return index >= 0 ? index + 1 : Object.values(Lang).indexOf(Lang.en) + 1;
};

/** The server trims before its Required check, so "   " must fail here too. */
export const requiredNonBlank = (value: string): boolean => value.trim().length > 0;
